import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useLanguage } from '../../context/LanguageContext';

const { FiMenu, FiX } = FiIcons;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => document.body.classList.remove('overflow-hidden');
  }, [isOpen]);

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.services'), path: '/services' },
    { name: t('nav.courses'), path: '/courses' },
    { name: t('nav.portfolio'), path: '/portfolio' },
    { name: t('nav.about'), path: '/about' },
  ];

  return (
    <>
      <header 
        className={`fixed w-full top-0 left-0 z-[100] transition-all duration-300 ${
          scrolled || isOpen ? 'bg-primary/95 backdrop-blur-md shadow-sm py-4' : 'bg-transparent py-6'
        }`}
      >
        <div className="container mx-auto px-5 md:px-12 flex justify-between items-center">
          <Link 
            to="/" 
            className="font-serif text-xl sm:text-2xl font-medium tracking-wide z-[110] relative"
            onClick={() => setIsOpen(false)}
          >
            Nailed by <span className="italic">Liana</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;

              return (
                <Link
                  key={link.name}
                  to={link.path}
                  className="group inline-flex flex-col items-start text-sm uppercase tracking-widest text-text-dark font-medium"
                >
                  <span>{link.name}</span>
                  <span
                    className={`mt-1 h-0.5 bg-text-dark transition-all duration-300 ${
                      isActive ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                  />
                </Link>
              );
            })}
            <select
              aria-label="Language selector"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="px-3 py-2 border border-text-dark bg-transparent text-sm uppercase tracking-widest font-medium focus:outline-none"
            >
              <option value="en">EN</option>
              <option value="fr">FR</option>
            </select>
            <Link 
              to="/contact" 
              className="px-6 py-2 border border-text-dark hover:bg-text-dark hover:text-white transition-all text-sm uppercase tracking-widest font-medium"
            >
              {t('nav.bookNow')}
            </Link>
          </nav>

          <button 
            className="md:hidden z-[110] relative p-1 focus:outline-none" 
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle Menu"
          >
            <SafeIcon icon={isOpen ? FiX : FiMenu} className="text-2xl" />
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-primary z-[105] md:hidden"
          >
            <motion.div 
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="flex flex-col items-center justify-center h-full gap-8 px-6"
            >
              {navLinks.map((link, index) => {
                const isActive = location.pathname === link.path;

                return (
                  <motion.div
                    key={link.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={link.path}
                      className="group inline-flex flex-col items-center text-3xl font-serif text-text-dark"
                      onClick={() => setIsOpen(false)}
                    >
                      <span>{link.name}</span>
                      <span
                        className={`mt-2 h-0.5 bg-text-dark transition-all duration-300 ${
                          isActive ? 'w-full' : 'w-0 group-hover:w-full'
                        }`}
                      />
                    </Link>
                  </motion.div>
                );
              })}
              <div className="w-full max-w-xs">
                <select
                  aria-label="Language selector"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full px-4 py-4 border border-text-dark bg-transparent text-center text-sm uppercase tracking-widest font-medium focus:outline-none"
                >
                  <option value="en">EN</option>
                  <option value="fr">FR</option>
                </select>
              </div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: navLinks.length * 0.1 }}
                className="w-full max-w-xs mt-4"
              >
                <Link 
                  to="/contact" 
                  className="block w-full py-4 bg-text-dark text-white text-center uppercase tracking-widest text-sm font-semibold"
                  onClick={() => setIsOpen(false)}
                >
                  {t('nav.bookAppointment')}
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
